import React, { Suspense, useEffect, useState } from 'react';
import Router from './routes';
import i18n from './components/i18n';
import 'react-toastify/dist/ReactToastify.css';
import '@assets/style.css';
import { I18nextProvider } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import dayjs from 'dayjs';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle, theme } from './Theme';
import AuthService from './api/services/auth';
import Bowser from 'bowser';
import { compressImage, getBase64SizeInMB, handleErrors } from './utils/helpers';
import DoodleComponent from './components/LoadingPage';
import axios from 'axios';
import environment from './environment';

export const UpdateThemeContext = React.createContext(null);

const Application: React.FunctionComponent = () => {
  const [updatedTheme, setUpdatedTheme] = useState({ ...theme })
  const [isBusy, setIsBusy] = useState(true)

  const params = new URLSearchParams(window.location.search)
  const tokenParam = params.get('token')

  function handleUnload() {
    const newTabCount2 = localStorage.getItem('tabsOpen');
    //@ts-ignore
    if (newTabCount2 - 1 == 0) {
      window.localStorage.isMySessionActive = 'true';

    } else {
      window.localStorage.isMySessionActive = 'false';
    }
  }

  useEffect(() => {
    fetchAppDate()
    window.addEventListener('beforeunload', handleUnload);
    window.onunload = function (e) {
      const newTabCountA = localStorage.getItem('tabsOpen');
      if (newTabCountA !== null) {
        //@ts-ignore
        localStorage.setItem('tabsOpen', newTabCountA - 1);
      }
    };
    // define increment counter part
    const tabsOpen = localStorage.getItem('tabsOpen');
    if (!tabsOpen) {
      //@ts-ignore
      localStorage.setItem('tabsOpen', 1);
    } else {
      //@ts-ignore
      localStorage.setItem('tabsOpen', parseInt(tabsOpen) + parseInt(1));
    }

    if (document.referrer === '') { // user manually entered URL and pressed enter
      window.localStorage.isMySessionActive = 'false';
    } else if (performance.navigation.type == performance.navigation.TYPE_RELOAD) {
      window.localStorage.isMySessionActive = 'false';
    }
    else {
      const newTabCount2 = localStorage.getItem('tabsOpen');
      const value = localStorage.getItem('isMySessionActive');
      if (value == 'true') {
        //@ts-ignore
        if (newTabCount2 - 1 == 0) {
          localStorage.removeItem('token');
          localStorage.removeItem('tabsOpen');

        } else {
          window.localStorage.isMySessionActive = 'false';
        }
      }
    }
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [])

  function getTypeBase64Type(data) {
    return data?.split(';')[0]?.split(':')[1] || 'image/jpeg'
  }

  async function fetchAppDate() {
    const languageStored = localStorage.getItem('i18nextLng')
    try {
      if (!localStorage.getItem('token') || !localStorage.getItem('theme')) {
        const responseTermsData = await AuthService.getSettingsData({ languageCode: languageStored || 'de' })
        const responseLogoImage = await AuthService.getLogo();
        const responseCoverImage = await AuthService.getCoverImage();
        const responseFaviconImage = await AuthService.getFavicon();
        if (!languageStored) {
          dayjs.locale(responseTermsData.data.code || 'de')
          i18n.changeLanguage(responseTermsData.data.code || 'de')
        }
        document.title = responseTermsData.data?.headerTitle || 'Food Business Digital'
        const favIconType = getTypeBase64Type(responseFaviconImage.data)
        const logoType = getTypeBase64Type(responseLogoImage.data)

        const isSafari = Bowser.getParser(window.navigator.userAgent).getBrowserName() === 'Safari';
        const compressedCoverImage = await compressImage(responseCoverImage.data, 1920, 1080, isSafari ? 0.7 : 1);
        const compressedLogoImage = getBase64SizeInMB(responseLogoImage.data) > 2 ? await compressImage(responseLogoImage.data, 800, 600, isSafari ? 0.8 : 1, logoType) : responseLogoImage.data;
        const compressedFavImage = await compressImage(responseFaviconImage.data, 800, 600, isSafari ? 0.8 : 1, favIconType);
        const newTheme = {
          ...updatedTheme,
          ...responseTermsData.data,
          logoTitle: compressedLogoImage,
          cover: compressedCoverImage,
          favIcon: compressedFavImage
        }
        setUpdatedTheme(newTheme)
        try {
          localStorage.setItem('theme', JSON.stringify(newTheme))
        } catch (e) {
          console.warn('localStorage quota exceeded, theme not cached', e)
        }
      } else {
        const savedTheme = JSON.parse(localStorage.getItem('theme')) || theme
        document.title = savedTheme?.headerTitle || 'Food Business Digital'
        setUpdatedTheme({ ...savedTheme })
      }
    } catch (error) {
      handleErrors(error)
      setIsBusy(false)
    } finally {
      if(tokenParam) {
        try {
          localStorage.removeItem('token')
          setIsBusy(true)
          const response = await axios.post(
            `${environment.API_URL}/verify-jwt-token`,
            {},
            {
              headers: { 'Authorization': tokenParam }
            });
          if (response.data.isValid) {
            localStorage.setItem('token', response.data.token)
          }
        }catch(error) {
          handleErrors(error)
          localStorage.removeItem('token')
        }
        setIsBusy(false)
      } else {
        setIsBusy(false)
      }
    }
  }

  if (isBusy && !localStorage.getItem('token')) {
    return <DoodleComponent />
  }

  return (
    <Suspense fallback={<div>Internal error</div>}>
      <UpdateThemeContext.Provider value={{ setUpdatedTheme }}>
        <ThemeProvider theme={updatedTheme}>
          <GlobalStyle></GlobalStyle>
          <ToastContainer
            pauseOnFocusLoss={false}
            autoClose={5000}
            closeButton={false}
            hideProgressBar={false}
            newestOnTop={false}
            pauseOnHover={false}
            rtl={false}
            draggable
            position={toast.POSITION.TOP_RIGHT}
          />
          <I18nextProvider i18n={i18n}>
            <Router />
          </I18nextProvider>
        </ThemeProvider>
      </UpdateThemeContext.Provider>
    </Suspense>
  );
};

export default Application;
